/* eslint-disable max-lines */
import { useContext, useCallback, useMemo } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { getApplyCouponWidgetData } from "@cart-client/helpers/Coupon";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { clickAddMoreAndSaveMore, impressionAddMoreAndSaveMore } from "@cart-client/analytics/events/CrossSellNudge";
import {
    impressionCrossSellM2Item,
    impressionCartItemDetails,
    impressionMagicItemsUnlockPopup,
    clickDismissMagicItemsUnlockPopup,
    clickExploreMenuMagicItems,
} from "@cart-client/analytics/events";

export function useCrossSellAnalytics(cartResponse: ReviewedCart | null) {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const applyCouponWidgetData = useMemo(() => getApplyCouponWidgetData(cartResponse), [cartResponse]);
    const { amount_to_add: amountToAdd, coupon_code: couponCode, savings } = applyCouponWidgetData || {};

    const onImpressionAddMoreAndSaveMore = useCallback(
        (isCouponWidget: boolean) => {
            if (cartResponse && applyCouponWidgetData) {
                impressionAddMoreAndSaveMore({
                    amountToAdd: amountToAdd || "",
                    couponCode: couponCode || "",
                    couponSavings: savings || "",
                    isCouponWidget,
                    cartId: String(cartResponse.data?.cartId || ""),
                    impressionEvent,
                });
            }
        },
        [amountToAdd, couponCode, savings, cartResponse, impressionEvent, applyCouponWidgetData],
    );

    const onClickAddMoreAndSaveMore = useCallback(
        (isCouponWidget: boolean) => {
            if (cartResponse && applyCouponWidgetData) {
                clickAddMoreAndSaveMore({
                    amountToAdd: amountToAdd || "",
                    couponCode: couponCode || "",
                    couponSavings: savings || "",
                    isCouponWidget,
                    cartId: String(cartResponse.data?.cartId || ""),
                    clickEvent,
                });
            }
        },
        [amountToAdd, couponCode, savings, cartResponse, clickEvent, applyCouponWidgetData],
    );

    const onImpressionCrossSellM2Item = useCallback(
        (params: { item: string; cart: string; basePrice?: number; slashedPrice?: number; position?: string }) => {
            impressionCrossSellM2Item(impressionEvent, params);
        },
        [impressionEvent],
    );

    const onImpressionCartItemDetails = useCallback(
        (itemId: string): void => {
            if (cartResponse?.data?.cartId) {
                impressionCartItemDetails(impressionEvent, String(cartResponse.data.cartId), itemId);
            }
        },
        [impressionEvent, cartResponse?.data?.cartId],
    );

    const onImpressionMagicItemsUnlockPopup = useCallback((): void => {
        if (cartResponse?.data?.cartId) {
            impressionMagicItemsUnlockPopup(impressionEvent, String(cartResponse.data.cartId));
        }
    }, [impressionEvent, cartResponse?.data?.cartId]);

    const onClickDismissMagicItemsUnlockPopup = useCallback((): void => {
        if (cartResponse?.data?.cartId) {
            clickDismissMagicItemsUnlockPopup(clickEvent, String(cartResponse.data.cartId));
        }
    }, [clickEvent, cartResponse?.data?.cartId]);

    const onClickExploreMenuMagicItems = useCallback((): void => {
        if (cartResponse?.data?.cartId) {
            clickExploreMenuMagicItems(clickEvent, String(cartResponse.data.cartId));
        }
    }, [clickEvent, cartResponse?.data?.cartId]);

    return {
        onImpressionAddMoreAndSaveMore,
        onClickAddMoreAndSaveMore,
        onImpressionCrossSellM2Item,
        onImpressionCartItemDetails,
        onImpressionMagicItemsUnlockPopup,
        onClickDismissMagicItemsUnlockPopup,
        onClickExploreMenuMagicItems,
    };
}
